import { memo, PointerEvent as ReactPointerEvent, useCallback, useEffect, useRef, useState } from 'react';

import { CanvasElement } from '@/features/canvas/types';
import { connectorGeometry } from './geometry';

/**
 * A connector's text label (Miro's "T+"), rendered in the world layer at the middle of the path so it
 * scales with zoom. Double-click a connector or use the toolbar's T+ to edit.
 */
function ConnectorLabel({
  connector,
  from,
  to,
  editing,
  draggable = false,
  onLabelDown,
  onCommit,
  onEditEnd,
}: {
  connector: CanvasElement;
  from: CanvasElement | undefined;
  to: CanvasElement | undefined;
  editing: boolean;
  draggable?: boolean;
  onLabelDown?: (e: ReactPointerEvent, id: string) => void;
  onCommit: (id: string, text: string) => void;
  onEditEnd: () => void;
}) {
  const [draft, setDraft] = useState(connector.content ?? '');
  const taRef = useRef<HTMLTextAreaElement>(null);
  const draftRef = useRef(draft);
  draftRef.current = draft;
  const contentRef = useRef(connector.content ?? '');
  contentRef.current = connector.content ?? '';
  const committedRef = useRef(false);

  // Commit on any exit — blur, Escape/Enter, click-away, or unmount (React skips blur on unmount, so
  // the cleanup is what saves a label when a background click removes the textarea). Guard prevents a
  // double commit when both the blur and the cleanup fire.
  const commit = useCallback(() => {
    if (committedRef.current) return;
    committedRef.current = true;
    if (draftRef.current !== contentRef.current) onCommit(connector.id, draftRef.current.trim());
    onEditEnd();
  }, [connector.id, onCommit, onEditEnd]);
  const commitRef = useRef(commit);
  commitRef.current = commit;

  useEffect(() => {
    if (!editing) return undefined;
    committedRef.current = false;
    setDraft(contentRef.current);
    taRef.current?.focus();
    taRef.current?.select();
    return () => commitRef.current();
  }, [editing]);

  if (!from || !to) return null;
  const g = connectorGeometry(from, to, connector.style ?? {});
  const anchor = g.labelAnchor;
  const font = connector.style?.fontSize ?? 14;

  return (
    <div
      style={{
        position: 'absolute',
        left: anchor.x,
        top: anchor.y,
        transform: 'translate(-50%, -50%)',
        cursor: editing ? 'text' : draggable ? 'move' : 'pointer',
        touchAction: 'none',
      }}
      // When not editing, a press starts a drag ALONG the connector (and selects it); a plain click just
      // selects. While editing, swallow the press so it doesn't start a drag / clear the selection.
      onPointerDown={(e) => {
        if (editing || !onLabelDown) {
          e.stopPropagation();
          return;
        }
        onLabelDown(e, connector.id);
      }}
    >
      {editing ? (
        <textarea
          ref={taRef}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => {
            if (e.key === 'Escape' || (e.key === 'Enter' && !e.shiftKey)) {
              e.preventDefault();
              commit();
            }
            e.stopPropagation();
          }}
          rows={1}
          className="resize-none rounded border-0 bg-white px-1 py-0.5 text-center text-[#1a1a1a] outline outline-2 outline-accent"
          style={{ fontSize: font, minWidth: 48, lineHeight: 1.2 }}
        />
      ) : connector.content ? (
        <div className="rounded bg-surface px-1 py-0.5 text-center text-fg" style={{ fontSize: font, lineHeight: 1.2 }}>
          {connector.content}
        </div>
      ) : null}
    </div>
  );
}

export default memo(ConnectorLabel);
